import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useMemo } from 'react';
import Decimal from 'decimal.js';
import { Spinner, Typography, WithInfoIcon } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { SalesSummaryStatus } from '~/models';
import { salesSummaryTextsPath } from '~/models/SalesSummaryModel';
import { selectors } from '~/store/state-selectors';
import { getLastXMonths } from '~/utils/date.utils';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import GraphWidgetTopCard from '../GraphWidget/GraphWidgetTopCard';
import { useSalesSummary } from './hooks/useSalesSummary';
import styles from './SalesWidget.module.scss';
import { salesWidgetTextsPath } from './salesWidget.utils';
export const GetSalesWidgetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            SalesWidgetTID: {
                Root: 'sales-widget',
                ProposalsSentThisYear: 'sales-widget-proposals-sent-this-year',
                MedianTimeToSign: 'sales-widget-median-time-to-sign',
                MonthlySales: (suffix)=>"monthly-sales".concat(suffix !== null && suffix !== void 0 ? suffix : ''),
                Chart: 'sales-widget-chart',
                MockChart: 'sales-widget-mock-chart',
                Error: 'widget-error'
            }
        }
    });
const SalesWidgetChartInner = /*#__PURE__*/ lazy(()=>import(/* webpackChunkName: "recharts" */ './SalesWidgetChartInner').then((module)=>({
            default: module.SalesWidgetChartInner
        })));
function SalesWidgetChart(param) {
    let { testId, data, isTestMode } = param;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        className: styles.chart,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(Spinner, {
                size: 30
            }),
            children: /*#__PURE__*/ _jsx(SalesWidgetChartInner, {
                testId: testId,
                data: data,
                isTestMode: isTestMode
            })
        })
    });
}
const TopCard = (param)=>{
    let { label, info, value, loading, testId, linkTo } = param;
    return /*#__PURE__*/ _jsx(GraphWidgetTopCard, {
        testId: testId,
        linkTo: linkTo,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.topCard,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                        info: info,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            children: label
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    weight: "normal",
                    testId: "".concat(testId, "-value"),
                    children: loading ? /*#__PURE__*/ _jsx(Spinner, {
                        size: 30,
                        testId: "".concat(testId, "-loading")
                    }) : value
                })
            ]
        })
    });
};
function getMockChartData(now) {
    return getLastXMonths(now, 12).map((date)=>({
            date,
            statuses: {
                [SalesSummaryStatus.Sent]: {
                    total: new Decimal(2)
                },
                [SalesSummaryStatus.Approved]: {
                    total: new Decimal(3)
                },
                [SalesSummaryStatus.Terminated]: {
                    total: new Decimal(0)
                },
                [SalesSummaryStatus.Canceled]: {
                    total: new Decimal(1)
                },
                [SalesSummaryStatus.Reviewed]: {
                    total: new Decimal(1)
                },
                [SalesSummaryStatus.Expired]: {
                    total: new Decimal(0)
                }
            }
        }));
}
function MockSalesWidgetChart(param) {
    let { isTestMode } = param;
    const { getNow } = useIoContext();
    const { SalesWidgetTID } = useMemo(()=>GetSalesWidgetTID(), []);
    const data = useMemo(()=>getMockChartData(getNow()), []);
    return /*#__PURE__*/ _jsx(SalesWidgetChart, {
        data: data,
        testId: SalesWidgetTID.MockChart(),
        isTestMode: isTestMode
    });
}
function SalesWidgetContent(param) {
    let { loading, data, isTestMode } = param;
    const hasSentAProposal = useStore(selectors.hasSentAProposal);
    const { t: salesWidgetTexts } = useLocaleKeys(salesWidgetTextsPath);
    const { t: salesSummaryTexts } = useLocaleKeys(salesSummaryTextsPath);
    const { SalesWidgetTID } = useMemo(()=>GetSalesWidgetTID(), []);
    const areCardsLoading = !data;
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        title: salesWidgetTexts.title(),
        loading: loading,
        bottomHeight: "l",
        testId: SalesWidgetTID.Root(),
        children: [
            /*#__PURE__*/ _jsx(TopCard, {
                loading: areCardsLoading,
                value: data ? data.formattedNumProposalsSentThisYear() : '',
                testId: SalesWidgetTID.ProposalsSentThisYear(),
                label: salesWidgetTexts.proposalsSentThisYear.label(),
                info: salesWidgetTexts.proposalsSentThisYear.info(),
                linkTo: routes.proposalsReport
            }),
            /*#__PURE__*/ _jsx(TopCard, {
                loading: areCardsLoading,
                value: data ? data.formattedMedianTimeToSign(salesSummaryTexts) : '',
                testId: SalesWidgetTID.MedianTimeToSign(),
                label: salesWidgetTexts.medianTimeToSign.label(),
                info: salesWidgetTexts.medianTimeToSign.info()
            }),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                testId: SalesWidgetTID.MonthlySales(areCardsLoading ? '-loading' : ''),
                loading: areCardsLoading,
                loadingMsg: salesWidgetTexts.loadingMsg(),
                empty: data && data.isEmpty() && !hasSentAProposal,
                emptyMsg: salesWidgetTexts.emptyMsg(),
                loadingOrEmptyBg: ()=>/*#__PURE__*/ _jsx(MockSalesWidgetChart, {
                        isTestMode: isTestMode
                    }),
                children: data && /*#__PURE__*/ _jsx(SalesWidgetChart, {
                    data: data.calendar(),
                    testId: SalesWidgetTID.Chart(),
                    isTestMode: isTestMode
                })
            })
        ]
    });
}
export default function SalesWidget(param) {
    let { isTestMode } = param;
    const { salesSummary, isLoading, isFetching, error } = useSalesSummary({
        refreshOnHookMount: true
    });
    const { SalesWidgetTID } = useMemo(()=>GetSalesWidgetTID(), []);
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: SalesWidgetTID.Error()
    });
    if (isLoading) return /*#__PURE__*/ _jsx(SalesWidgetContent, {
        loading: true,
        isTestMode: isTestMode
    });
    return /*#__PURE__*/ _jsx(SalesWidgetContent, {
        loading: isFetching,
        data: salesSummary,
        isTestMode: isTestMode
    });
}
